import { api } from "@/api";
import { readReferralPromo } from "@/lib/referral-promo";
import { setBuyerAuthToken } from "@/api/token";
import type {
	BuyerAuthStartRequest,
	BuyerAuthStartResponse,
	BuyerAuthVerifyRequest,
	BuyerMe,
	BuyerProfile,
	BuyerProfilePatch,
	User,
} from "@/api/types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { attachDeviceToAccount, resetDevice } from "./device";
import { safeLocalStorage } from "./safe-storage";
import { METRIKA_GOALS, reachGoal } from "@/lib/metrika-ecommerce";

export type BuyerUser = Pick<User, "id" | "name" | "email" | "phone" | "role">;

type ProfileState = {
	token: string | null;
	user: BuyerUser | null;
	profile: BuyerProfile | null;
	orderIds: string[];
	lastAuthStart: BuyerAuthStartResponse | null;
	isHydrating: boolean;
	setBuyerSession: (input: { token: string; user: User; profile: BuyerProfile }) => void;
	startPhoneAuth: (input: BuyerAuthStartRequest) => Promise<BuyerAuthStartResponse>;
	verifyPhoneAuth: (input: BuyerAuthVerifyRequest) => Promise<BuyerMe | null>;
	/** Вход через Яндекс ID / VK ID: билет уже обменян на токен (lib/oauth). */
	acceptExternalSession: (input: { token: string; user: User; profile: BuyerProfile; isNewUser?: boolean }) => Promise<void>;
	hydrateMe: () => Promise<BuyerMe | null>;
	updateProfile: (input: BuyerProfilePatch) => Promise<BuyerMe>;
	signInMock: () => void;
	signOut: () => Promise<void>;
	addOrder: (orderId: string) => void;
};

const toBuyerUser = (user: User, profile: BuyerProfile): BuyerUser => ({
	id: user.id,
	name: profile.name || user.name,
	email: profile.email ?? user.email,
	phone: profile.phone || user.phone,
	role: user.role,
});

export const useProfile = create<ProfileState>()(
	persist(
		(set, get) => ({
			token: null,
			user: null,
			profile: null,
			orderIds: [],
			lastAuthStart: null,
			isHydrating: false,
			setBuyerSession: ({ token, user, profile }) => {
				setBuyerAuthToken(token);
				set({ token, user: toBuyerUser(user, profile), profile });
			},
			startPhoneAuth: async (input) => {
				const response = await api.buyer.startAuth(input);
				set({ lastAuthStart: response });
				return response;
			},
			verifyPhoneAuth: async (input) => {
				// Пришёл по партнёрской ссылке — код уходит вместе со входом.
				const promo = readReferralPromo();
				const response = await api.buyer.verifyAuth(promo ? { ...input, promo } : input);
				if (!response.success) return null;
				get().setBuyerSession(response);
				if (response.isNewUser) reachGoal(METRIKA_GOALS.registration);
				// Вход = регистрация по телефону: накопленное гостем на этом
				// устройстве (корзина, избранное, просмотры) — в аккаунт. Ошибки
				// слияния вход не роняют (attachDeviceToAccount их гасит).
				await attachDeviceToAccount();
				return { user: response.user, profile: response.profile };
			},
			acceptExternalSession: async (response) => {
				get().setBuyerSession(response);
				if (response.isNewUser) reachGoal(METRIKA_GOALS.registration);
				// Как после входа по SMS: накопленное гостем - в аккаунт.
				await attachDeviceToAccount();
			},
			hydrateMe: async () => {
				const token = get().token;
				if (!token) return null;
				setBuyerAuthToken(token);
				set({ isHydrating: true });
				try {
					const me = await api.buyer.me();
					set({ user: toBuyerUser(me.user, me.profile), profile: me.profile });
					return me;
				} catch (error) {
					setBuyerAuthToken(null);
					set({ token: null, user: null, profile: null, orderIds: [] });
					// Сессия отозвана/протухла — то же, что выход: данных аккаунта
					// на устройстве не оставляем.
					resetDevice();
					throw error;
				} finally {
					set({ isHydrating: false });
				}
			},
			updateProfile: async (input) => {
				const me = await api.buyer.updateProfile(input);
				set({ user: toBuyerUser(me.user, me.profile), profile: me.profile });
				return me;
			},
			// Back-compat for the current profile page until T9 replaces the UI with
			// phone/SMS forms. It no longer fabricates a signed-in buyer in gateway mode.
			signInMock: () => {
				void get()
					.startPhoneAuth({ phone: "+79995554433", deviceName: "Mock browser" })
					.then((started) =>
						get().verifyPhoneAuth({
							sessionId: started.sessionId,
							code: "111111",
							deviceName: "Mock browser",
						}),
					);
			},
			signOut: async () => {
				try {
					if (get().token) await api.buyer.logout();
				} catch {
					// Local logout remains authoritative when the session is already
					// expired or the gateway is temporarily unavailable.
				} finally {
					setBuyerAuthToken(null);
					set({ token: null, user: null, profile: null, lastAuthStart: null, orderIds: [] });
					// После выхода на устройстве не остаётся данных аккаунта:
					// корзина аккаунта и его избранное забываются, это новый гость.
					resetDevice();
				}
			},
			addOrder: (orderId) =>
				set((s) => ({
					orderIds: s.orderIds.includes(orderId) ? s.orderIds : [orderId, ...s.orderIds],
				})),
		}),
		{
			name: "osmix-profile",
			storage: createJSONStorage(() => safeLocalStorage),
			partialize: (s) => ({
				token: s.token,
				user: s.user,
				profile: s.profile,
				orderIds: s.orderIds,
			}),
			onRehydrateStorage: () => (state) => {
				setBuyerAuthToken(state?.token ?? null);
			},
		},
	),
);
